import "../index.css";
import { useContext, useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { TbEye, TbEdit } from "react-icons/tb";
import { DateTime } from "luxon";
import { YearContext, useAuthContext } from "../context/context";
import Headline from "../Components/Elements/Headline";
import NavSidebar from "../Components/Sidebar/NavSidebar";
import StartMessage from "../Components/Elements/StartMessage";
import VisionBoard from "../Components/Elements/VisionBoard";
import Card from "../Components/Cards/Card";
import CardQuote from "../Components/Cards/CardQuote";
import CardTheme from "../Components/Cards/CardTheme";
import CardLetter from "../Components/Cards/CardLetter";
import TabSection from "../Components/Cards/CardTab";
import GoalCardOneRow from "../Components/Cards/CardGoalOneRow";
import CardMilestones from "../Components/Cards/CardMilestones";
import CardFinance from "../Components/Cards/CardFinance";
import CardWishlistMobile from "../Components/Cards/CardWishlistMobile";
import PageContentContainer from "../Components/ContentContainers/PageContentContainer";
import HomeContentContainer from "../Components/ContentContainers/HomeContentContainer";
import { useLanguage } from "../context/LanguageContext";
import Toast from "../Components/Elements/Toast";
import Dropdown from "../Components/Elements/Dropdown";
import Lottie from "lottie-react";
import loadingAnimation from "../Animations/load-animation.json";
import { fetchMilestones } from "../api";

export default function Home() {
  const { selectedYear } = useContext(YearContext);
  const [sidebarToggled, setSidebarToggled] = useState(false);
  const { userData, setUserData } = useAuthContext();
  const { copy } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [goals, setGoals] = useState(
    Array.isArray(userData?.goals) ? userData.goals : []
  );
  // Initialize isDataReady based on whether userData already exists
  const [isDataReady, setIsDataReady] = useState(Boolean(userData && userData.id));
  const [milestonesLoading, setMilestonesLoading] = useState(false);

  const [toast, setToast] = useState(null);

  // Create stable reference for goals to prevent unnecessary re-renders
  const goalsKey = useMemo(() => {
    return goals.map(g => `${g.id}-${g.title}-${g.year}`).join(',');
  }, [goals]);

  // Helper functions for milestone data caching
  const getMilestonesCacheKey = () => {
    return userData?.id ? `milestones_cache_${userData.id}` : null;
  };

  const getCachedMilestones = () => {
    try {
      const cacheKey = getMilestonesCacheKey();
      if (!cacheKey) return null;

      const cachedData = localStorage.getItem(cacheKey);
      if (!cachedData) return null;

      const parsedData = JSON.parse(cachedData);
      const now = new Date().getTime();
      const cacheExpiry = 60 * 60 * 1000; // 1 hour in milliseconds
      const CACHE_VERSION = 2; // Increment this to invalidate old cache

      // Check cache version - if old version or no version, clear it
      if (!parsedData.version || parsedData.version < CACHE_VERSION) {
        localStorage.removeItem(cacheKey);
        return null;
      }

      // Check if cache is still valid (less than 1 hour old)
      if (now - parsedData.timestamp < cacheExpiry) {
        return parsedData.data;
      } else {
        // Cache expired, remove it
        localStorage.removeItem(cacheKey);
        return null;
      }
    } catch (error) {
      return null;
    }
  };

  const setCachedMilestones = (data) => {
    try {
      const cacheKey = getMilestonesCacheKey();
      if (!cacheKey) return;

      const CACHE_VERSION = 2; // Must match getCachedMilestones version
      const cacheData = {
        version: CACHE_VERSION,
        data,
        timestamp: new Date().getTime()
      };

      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      // Silently fail
    }
  };

  // Export cache clearing function so CardMilestones can use it
  window.clearMilestonesCache = () => {
    try {
      const cacheKey = userData?.id ? `milestones_cache_${userData.id}` : null;
      if (cacheKey) {
        localStorage.removeItem(cacheKey);
      }
    } catch (error) {
      // Silently fail
    }
  };

  useEffect(() => {
    if (userData && userData.id) {
      setGoals(Array.isArray(userData?.goals) ? userData.goals : []);
      // Mark data as ready immediately if userData exists
      setIsDataReady(true);
    } else {
      setIsDataReady(false);
    }
  }, [userData?.id, userData?.goals]);

  // Fetch all milestones when component mounts
  useEffect(() => {
    const loadMilestones = async () => {
      if (!userData || !userData.id) {
        return;
      }

      // Check if current milestones already have status field (complete data)
      const currentMilestones = userData.milestones;
      const hasCompleteData = currentMilestones &&
        Array.isArray(currentMilestones) &&
        currentMilestones.length > 0 &&
        currentMilestones[0].hasOwnProperty('status');

      if (hasCompleteData) {
        // Already have complete data, no need to fetch
        return;
      }

      // Try to get complete data from cache
      const cachedData = getCachedMilestones();
      if (cachedData && Array.isArray(cachedData)) {
        setUserData(prev => ({
          ...prev,
          milestones: cachedData
        }));
        return;
      }

      // No complete data - fetch all milestones
      try {
        setMilestonesLoading(true);
        const milestones = await fetchMilestones();

        if (milestones && Array.isArray(milestones)) {
          // Update userData context with complete milestone data
          setUserData(prev => ({
            ...prev,
            milestones: milestones
          }));
          // Cache the data
          setCachedMilestones(milestones);
        }
      } catch (error) {
        // Silently handle error
      } finally {
        setMilestonesLoading(false);
      }
    };

    loadMilestones();
  }, [userData?.id, userData?.milestones, setUserData]);

  useEffect(() => {
    if (location.state?.toast) {
      setToast(location.state.toast);
      // Clear navigation state so the toast doesn't show again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Handle subscription success/cancel from Stripe redirect
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const subscriptionStatus = searchParams.get('subscription');

    if (subscriptionStatus === 'success') {
      // Show success message
      setToast({
        message: copy.pricing?.subscription_success || 'Subscription activated successfully!',
        type: 'success'
      });

      // Refresh user data to get updated subscription status
      const refreshUserData = async () => {
        try {
          const response = await fetch(`${import.meta.env.VITE_API_URL}/getuser`, {
            credentials: 'include',
          });
          if (response.ok) {
            const authData = await response.json();
            setUserData(authData.data);
          }
        } catch (error) {
          console.error('Error refreshing user data:', error);
        }
      };

      refreshUserData();

      // Clean up URL
      window.history.replaceState({}, document.title, '/');
    } else if (subscriptionStatus === 'cancel') {
      setToast({
        message: copy.pricing?.subscription_cancelled || 'Subscription checkout was cancelled.',
        type: 'error'
      });

      // Clean up URL
      window.history.replaceState({}, document.title, '/');
    }
  }, [location.search, copy, setUserData]);

  // Show loading screen if data is not ready yet
  if (!isDataReady) {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center">
        <div className="text-center">
          <Lottie
            animationData={loadingAnimation}
            loop
            autoplay
            style={{ width: 300, height: 300 }}
          />
          <p className="text-center text-lg">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div key={`home-${userData?.id || 'no-user'}`}>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {goals.length === 0 ? (
        <PageContentContainer>
          <StartMessage
            title={copy.start_message.headline}
            message={copy.start_message.description}
          />
        </PageContentContainer>
      ) : (
        <HomeContentContainer className="flex flex-col md:flex-row h-auto md:h-screen gap-2 md:gap-2 px-2 md:px-0 py-4 md:py-0">
          <div className="flex flex-col gap-2 md:gap-2 lg:w-2/3 w-full">
            {/* Headline - Only visible on desktop/tablet */}
            <div className="hidden md:block">
              <Headline
                headline={`${copy.homepage.headline} ${selectedYear}`}
                description={copy.homepage.description}
              />
            </div>

            {/* Mobile Milestone Progress Component */}
            <div className="md:hidden">
              {milestonesLoading ? (
                <div className="card-base card-base-border bg-white p-6 flex flex-col items-center justify-center gap-3">
                  <Lottie
                    animationData={loadingAnimation}
                    loop
                    autoplay
                    style={{ width: 150, height: 150 }}
                  />
                  <p className="text-center text-sm color-text-secondary">Loading progress...</p>
                </div>
              ) : (
                (() => {
                  const milestones = Array.isArray(userData?.milestones) ? userData.milestones : [];
                  const yearlyMilestones = milestones.filter((milestone) => {
                    const goal = userData?.goals?.find((g) => g.id === milestone.goal_id);
                    return goal && goal.year === selectedYear;
                  });

                  const completedMilestones = yearlyMilestones.filter(m => m.status === true || m.status === "completed").length;
                  const totalMilestones = yearlyMilestones.length;
                  const progressPercentage = totalMilestones > 0 ? Math.round((completedMilestones / totalMilestones) * 100) : 0;

                  // Create a mock goal object for the component
                  const mockGoal = {
                    id: 'milestone-progress',
                    title: 'Progress',
                    year: selectedYear
                  };

                  const customDropdown = [
                    {
                      text: "View goals",
                      onClick: () => window.location.href = '/goals',
                      icon: <TbEye />,
                    },
                  ];

                  return (
                    <GoalCardOneRow
                      goal={mockGoal}
                      title={`Progress ${selectedYear}`}
                      goalMilestones={yearlyMilestones}
                      onEdit={() => {}} // Empty function for now
                      onDelete={() => {}} // Empty function for now
                      customDropdown={customDropdown}
                      titleSize="md:text-xl text-xl"
                      hideDropdown={true}
                    />
                  );
                })()
              )}
            </div>

            <div className="h-56 xs:h-72 sm:h-80 md:h-[450px] overflow-hidden card-base card-base-border relative">
              <VisionBoard isDraggable={false} isResizable={false} />

              {/* Dropdown menu - visible on both mobile and desktop */}
              <div className="absolute top-4 right-4 z-10">
                <Dropdown
                  iconComponent="circle"
                  options={[
                    {
                      text: "Edit",
                      icon: <TbEdit />,
                      onClick: () => navigate('/vision-board'),
                    },
                  ]}
                />
              </div>
            </div>

            {/* Mobile Cards - Only visible on mobile */}
            <div className="md:hidden">
              <Card
                title="Describe your year"
                description="Reflect on your year with quotes, themes, and letters"
                carousel={true}
              >
                <div className="flex-shrink-0 w-[90%] sm:w-[75%]">
                  <CardQuote />
                </div>
                <div className="flex-shrink-0 w-[90%] sm:w-[75%]">
                  <CardTheme />
                </div>
                <div className="flex-shrink-0 w-[90%] sm:w-[75%]">
                  <CardLetter />
                </div>
              </Card>
            </div>

            {/* Mobile Today's To-dos - Only visible on mobile */}
            <div className="md:hidden">
              <CardMilestones 
                activeDay={DateTime.local()}
                isInTab={true}
                showViewInCalendarButton={false}
                customTitle={copy.card_milestone.headline}
                customDescription={copy.card_milestone.description}
              />
            </div>

            {/* Mobile Finance - Only visible on mobile */}
            <div className="md:hidden">
              <CardFinance 
                isInTab={true}
                showViewInFinanceButton={false}
                customTitle="Finances"
                customDescription="Monthly financial overview and budget tracking"
              />
            </div>

            {/* Mobile Wishlist - Only visible on mobile */}
            <div className="md:hidden !mb-10">
              <CardWishlistMobile 
                isInTab={true}
                showViewInWishlistButton={false}
                customTitle="Wishlist"
                customDescription="Your wishes and dreams for this year"
              />
            </div>

            <div className="flex-1 hidden md:block">
              <TabSection />
            </div>
          </div>
          {/* Desktop Cards - Only visible on desktop */}
          <div className="hidden md:grid lg:grid-rows-3 md:grid-rows-1 gap-4 md:gap-2 lg:w-1/3 w-full min-h-full mt-4 md:mt-0 items-stretch">
            <CardQuote />
            <CardTheme />
            <CardLetter />
          </div>
        </HomeContentContainer>
      )}

      {sidebarToggled && (
        <NavSidebar setSidebarToggled={() => setSidebarToggled(false)} />
      )}
    </div>
  );
}
